/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createCall,
  deleteCallById,
  deleteCallRecording as deleteCallRecordingRequest,
  getAllCalls,
  getBulkPresignedUrl,
  getCallId,
  startConferenceRecordingApi,
  stopConferenceRecordingApi,
  updateCallById,
  updateCallByNumber,
} from '../../services/api/calls.api'
import { ICallDirection, ICalls } from '@dataTypes/calls.type'
import type { IContact } from '@dataTypes/contact.type'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import type { RootState } from '@store/index'

export const setCallerInfo = createAsyncThunk<
  { callerInfo: Partial<ICalls> | null },
  Partial<ICalls> | null,
  {
    getState: () => RootState
    rejectValue: string
  }
>(
  'calls/setCallerInfo',
  async (callerInfoPayload, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const contacts = state.contacts?.data ?? []
      let callerInfo: Partial<ICalls> | null =
        callerInfoPayload != null ? { ...callerInfoPayload } : null
      if (callerInfo) {
        const number =
          callerInfo.call_type === 'Incoming'
            ? callerInfo.caller_number
            : callerInfo.receiver_number
        if (number && Array.isArray(contacts)) {
          const contact =
            (contacts as IContact[]).find((c) =>
              [
                c.cellphone ? normalizePhoneNumber(c.cellphone) : '',
                ...(c.home_phone ? [normalizePhoneNumber(c.home_phone)] : []),
                ...(c.work_phone ? [normalizePhoneNumber(c.work_phone)] : []),
              ].includes(normalizePhoneNumber(number)),
            ) ?? null
          if (contact) {
            callerInfo = { ...callerInfo, contact }
          }
        }
      }
      return { callerInfo }
    } catch (error: any) {
      return rejectWithValue(error.message || 'An error occurred')
    }
  },
)

export const fetchAllCallLogs = createAsyncThunk<
  ICalls[],
  void,
  {
    rejectValue: string
  }
>('api/calls/fetch/all', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllCalls()

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'calls' in response.data &&
      Array.isArray(response.data.calls)
    ) {
      return response.data.calls as ICalls[]
    }

    return rejectWithValue('Calls not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const generatePresignedUrlsForCalls = createAsyncThunk<
  ICalls[],
  ICalls[],
  {
    rejectValue: string
  }
>('api/calls/generate-presigned-urls', async (calls, { rejectWithValue }) => {
  try {
    if (!calls || calls.length === 0) {
      return calls
    }

    const response = await getBulkPresignedUrl(calls)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'calls' in response.data &&
      Array.isArray(response.data.calls)
    ) {
      return response.data.calls as ICalls[]
    }

    return rejectWithValue('Calls not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const saveCall = createAsyncThunk<
  { callerInfo: Partial<ICalls> },
  Partial<ICalls>,
  {
    rejectValue: string
  }
>('api/calls/post', async (callerInfo, { rejectWithValue }) => {
  try {
    const callId = (await getCallId(callerInfo)).data.call_id
    const response = await createCall({ ...callerInfo, call_id: callId })

    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to add calls')
    }
    return { callerInfo: response.data }
  } catch (error: any) {
    return rejectWithValue(error.meesage || 'An error occurred')
  }
})

export const updateCalls = createAsyncThunk<
  ICalls,
  { caller_number: string; receiver_number: string; updates: Partial<ICalls> },
  { rejectValue: string }
>(
  'api/calls/update',
  async ({ caller_number, receiver_number, updates }, { rejectWithValue }) => {
    try {
      const response = await updateCallByNumber(
        caller_number,
        receiver_number,
        updates,
      )

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'calls' in response.data &&
        typeof response.data.calls === 'object' &&
        !Array.isArray(response.data.calls)
      ) {
        return response.data.calls as ICalls
      }

      return rejectWithValue('Calls not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred when updating ')
    }
  },
)

export const updateCallsById = createAsyncThunk<
  ICalls,
  { call_id: string; updates: Partial<ICalls> },
  { rejectValue: string }
>('api/calls/update/id', async ({ call_id, updates }, { rejectWithValue }) => {
  try {
    const response = await updateCallById(call_id, updates)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'call' in response.data &&
      typeof response.data.call === 'object' &&
      !Array.isArray(response.data.call)
    ) {
      const call = response.data.call as ICalls

      if (
        ['Cancelled', 'Completed', 'Busy', 'No Answer', 'Failed'].includes(
          call['call_status'],
        )
      ) {
        localStorage.removeItem('prev_call_id')
      }
      return call
    }

    return rejectWithValue('Calls not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'Error occurred when updating ')
  }
})

export const addCall = createAsyncThunk<
  Partial<ICalls>,
  { callData: Partial<ICalls> },
  {
    rejectValue: string
  }
>('api/calls/create', async ({ callData }, { rejectWithValue }) => {
  try {
    const callId = (await getCallId(callData)).data.call_id
    const response = await createCall({ ...callData, call_id: callId })

    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to add calls')
    }

    return response.data
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const deleteCall = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/calls/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteCallById(id)

    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    return id
  } catch (error) {
    return rejectWithValue('Failed to delete call')
  }
})

export type ConferenceRecordingControlArgs = {
  sid: string
  token: string
  conferenceSid: string
  db_name: string
  clinic_id: string
  direction: ICallDirection
}

export const startConferenceRecording = createAsyncThunk<
  { conferenceSid: string },
  ConferenceRecordingControlArgs,
  { rejectValue: string }
>('api/calls/startConferenceRecording', async (body, { rejectWithValue }) => {
  try {
    const response = await startConferenceRecordingApi(body)
    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to start recording')
    }
    return { conferenceSid: body.conferenceSid }
  } catch {
    return rejectWithValue('Failed to start recording')
  }
})

export const stopConferenceRecording = createAsyncThunk<
  { conferenceSid: string },
  Pick<ConferenceRecordingControlArgs, 'sid' | 'token' | 'conferenceSid'>,
  { rejectValue: string }
>('api/calls/stopConferenceRecording', async (body, { rejectWithValue }) => {
  try {
    const response = await stopConferenceRecordingApi(body)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message || 'Failed to stop recording')
    }
    return { conferenceSid: body.conferenceSid }
  } catch {
    return rejectWithValue('Failed to stop recording')
  }
})

export const deleteCallRecording = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/calls/deleteRecording', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteCallRecordingRequest(id)

    if (response.statusCode !== 200) {
      return rejectWithValue(
        response.message || 'Failed to delete call recording',
      )
    }
    return id
  } catch (error) {
    return rejectWithValue('Failed to delete call recording')
  }
})
